import React from 'react';
import Button from '../molecules/Button';
import * as THREE from 'three';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader';
import { Utils } from '../3d/utils/utils';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { toast } from 'react-toastify';
import Stack, { getDifferencesWithValues, Path } from '../../utils/stack';
import {
    applyRuntimeCameraHeight,
    applyRuntimeFov,
    DEFAULT_RUNTIME_CAMERA_HEIGHT,
    getActiveRuntimeFov,
    getCurrentRuntimeCameraHeight,
    getMoodCameraHeight,
    RUNTIME_CAMERA_HEIGHT_MAX,
    RUNTIME_CAMERA_HEIGHT_MIN,
    RUNTIME_CAMERA_HEIGHT_STEP,
    RUNTIME_FOV_MAX,
    RUNTIME_FOV_MIN
} from './render.settings.camera';
import { isMoMPostProcessingTarget, normalizePostProcessingPasses } from '../3d/renderer/services/render.pass.utils';
import {
    DEFAULT_STROBE_FREQUENCY_HZ,
    MAX_STROBE_FREQUENCY_HZ,
    MIN_STROBE_FREQUENCY_HZ,
} from '../3d/utils/strobeLight.utils';

const utils = new Utils();


import {
    RenderPass, FXAAPass, BloomPass, LUTPass, DotScreenPass, NoisePass, GlitchPass, RenderArtworkPass,
    LightType, MaterialOverrideType, InfoSettings, SkySettings, MaterialOverride, LightSettings,
    FogSettings, RenderSettings, LightMapSettings, MoodSettings
} from '../../types/renderSettings';

type Props = {
    show: boolean;
    onHide: () => void;

    initialSettings?: Partial<MoodSettings>;
    currentMood?: MoodSettings;
    appData: any;
    onChangeMood: (newMood: number, local?: any) => void;
    onSelectMoodForGallery?: (moodID: number) => Promise<boolean>;
    onGetFilesFromFolder?: (folder: string) => Promise<string[]>;
    onUploadFilesToFolder?: (folder: string, files: File[]) => Promise<{ name: string, url: string }[]>;
    onApplyMood: (moodID: number, data: any) => Promise<any>;
    failedNotify: (message: string) => void;
}

// Store last edited moods to persist across modal reopens
// const lastEditedMoods = new Map();

// let localMood = {}

// // Define lasFogValues at the top level
// let lasFogValues: [number, number] = [0, 0];

// Define getLightByIndex function at the top level of the component
const getLightByIndex = (appData, index: number) => {
    const lightsHolder = appData?.spaceScene?.getObjectByName("lightsHolder");
    if (!lightsHolder || !Array.isArray(lightsHolder.children)) {
        return null;
    }
    return lightsHolder.children[index] ?? null;
};

// Update getMeshNames to use traverse
const getMeshNames = (appData) => {
    const meshNames = [];
    const materialIds = new Set(); // Track unique material IDs

    appData.spaceScene.traverse((child) => {
        if (child.isMesh && child.material && child.name &&
            !child.name.startsWith('octree-mesh') &&
            !child.name.startsWith('remove_Space-') &&
            !child.name.startsWith('entryTo_Space-') &&
            !child.name.startsWith('cover_Space-')) {

            // Get material ID - handle both single materials and material arrays
            const materialId = Array.isArray(child.material)
                ? child.material[0].uuid
                : child.material.uuid;

            // Only add mesh if we haven't seen this material before
            if (!materialIds.has(materialId)) {
                materialIds.add(materialId);
                meshNames.push(child.name);
            }
        }
    });

    // Sort the meshNames array alphabetically
    meshNames.sort();
    return meshNames;
};

export const RenderSettingsModal = ({ show, onHide, currentMood, appData, onChangeMood, onSelectMoodForGallery, onGetFilesFromFolder, onUploadFilesToFolder, onApplyMood, failedNotify }: Props) => {
    // Create a default settings object to ensure we always have valid values
    const defaultSettings: MoodSettings = {
        published: false,
        audienceType: "general",
        galleryId: "",
        galleryIds: [],
        users: [],
        debugCameraEnabled: false,
        cameraHeight: DEFAULT_RUNTIME_CAMERA_HEIGHT,
        name: "Default Mood",
        description: "Default Mood Setup",
        thumbnailUrl: "",
        fog: {
            enabled: true,
            near: 1.0,
            far: 100.0,
            color: "#ffffff",
            applyFogToArtworks: false
        },
        lights: [],
        materialOverrides: [],
        sky: {
            hdriUrl: "",
            skyImageUrl: "",
            skyModelName: "sky",
            hdriVisible: true,
            hdrIntensity: 1.0
        },
        renderSettings: {
            toneMapping: 1,
            toneMappingExposure: 1.0,
            postProcessing: [
                { type: "render" }
            ],
            artworkMapIntensity: 1.0
        },
        lightMap: {
            selectedMeshes: [],
            lightMapUrl: ""
        }
    };

    const ensureRenderPass = (postProcessing: any): any[] => normalizePostProcessingPasses(postProcessing, {
        forceFxaa: isMoMPostProcessingTarget(appData?.galleryData, appData?.spaceData)
    });

    const normalizeMoodForPersistence = (mood: MoodSettings): MoodSettings => {
        const normalizedGalleryIds = Array.isArray(mood.galleryIds)
            ? Array.from(new Set(mood.galleryIds.map((id) => String(id)).filter(Boolean)))
            : [];
        if (typeof mood.galleryId === "string" && mood.galleryId) {
            normalizedGalleryIds.push(String(mood.galleryId));
        }
        const dedupedGalleryIds = Array.from(new Set(normalizedGalleryIds));

        return {
            ...mood,
            cameraHeight: getMoodCameraHeight(mood),
            galleryIds: dedupedGalleryIds,
            galleryId: dedupedGalleryIds[0] || mood.galleryId || "",
            renderSettings: {
                ...mood.renderSettings,
                postProcessing: ensureRenderPass(mood.renderSettings?.postProcessing) as any
            }
        };
    };

    const [draggedItem, setDraggedItem] = React.useState<number | null>(null);
    const [selectedMood, setSelectedMood] = React.useState<string>("");
    const [availableMoods, setAvailableMoods] = React.useState<Array<{ name: string, id: string, mood: MoodSettings }>>([]);
    const [debugCameraEnabled, setDebugCameraEnabled] = React.useState<boolean>(false);
    const [runtimeFov, setRuntimeFov] = React.useState<number>(() => getActiveRuntimeFov(appData, Boolean(appData?.debugCameraEnabled)));
    const [runtimeCameraHeight, setRuntimeCameraHeight] = React.useState<number>(() => getCurrentRuntimeCameraHeight(appData));
    const isLightDragInProgressRef = React.useRef(false);


    // Modify the custom hook to return transformControlsRef
    const useTransformControls = (appData, flushDraggedLightPreview) => {
        const transformControlsRef = React.useRef<TransformControls | null>(null);
        const [draggingLight, setDraggingLight] = React.useState<{
            index: number;
            type: 'position' | 'target';
        } | null>(null);

        const disposeTransformControls = () => {
            if (!transformControlsRef.current) return;
            const helper = transformControlsRef.current.getHelper?.();
            if (helper?.parent) {
                helper.parent.remove(helper);
            }
            transformControlsRef.current.detach();
            transformControlsRef.current.dispose();
            transformControlsRef.current = null;
        };

        const handleStartDrag = (index: number, type: 'position' | 'target') => {
            disposeTransformControls();
            const light = getLightByIndex(appData, index);
            if (!light) {
                failedNotify("Cannot move this light right now. Please reopen Render Settings and try again.");
                setDraggingLight(null);
                isLightDragInProgressRef.current = false;
                return;
            }

            const lightHelper = appData.spaceScene.getObjectByName("lightHelper");
            let camera = appData.camera;
            if (debugCameraEnabled) {
                camera = appData.debugCamera;
            }
            transformControlsRef.current = new TransformControls(camera, appData.renderService.renderer.domElement);
            transformControlsRef.current.setMode("translate");
            transformControlsRef.current.setSize(0.9);

            // Attach to target if type is 'target' and light has a target
            if (type === 'target' && light.target) {
                transformControlsRef.current.attach(light.target);
            } else if (type === 'target' && !light.target) {
                failedNotify("Selected light does not have a target to drag.");
                disposeTransformControls();
                setDraggingLight(null);
                isLightDragInProgressRef.current = false;
                return;
            } else {
                transformControlsRef.current.attach(light);
            }

            setDraggingLight({ index, type });
            isLightDragInProgressRef.current = true;
            appData.renderService.addToRenderQueue();

            transformControlsRef.current.addEventListener('dragging-changed', function (event) {
                appData.renderService.controls.enabled = !event.value;
            });
            transformControlsRef.current.addEventListener("change", () => {
                appData.renderService.addToRenderQueue();

                // Find and update any light helpers for this light
                if (lightHelper?.children?.length) {
                    lightHelper.children.forEach(child => {
                        if ((child instanceof THREE.DirectionalLightHelper ||
                            child instanceof THREE.PointLightHelper ||
                            child instanceof THREE.SpotLightHelper)) {
                            child.update();
                        }
                    });
                }

                updateSettings(prev => {
                    const newSettings = {
                        ...prev,
                        lights: prev.lights.map((lght, i) =>
                            i === index ? {
                                ...lght,
                                position: {
                                    x: light.position.x,
                                    y: light.position.y,
                                    z: light.position.z
                                },
                                ...(light.target && {
                                    target: {
                                        x: light.target.position.x,
                                        y: light.target.position.y,
                                        z: light.target.position.z
                                    }
                                })
                            } : lght
                        )
                    };
                    return newSettings;
                });
            });

            const helper = transformControlsRef.current.getHelper?.();
            if (helper) {
                appData.spaceScene.add(helper);
            }
        };

        const handleEndDrag = (flushPreview: boolean = true) => {
            const wasDragging = isLightDragInProgressRef.current;
            disposeTransformControls();
            setDraggingLight(null);
            isLightDragInProgressRef.current = false;
            appData.renderService.controls.enabled = true;
            appData.renderService.addToRenderQueue();
            if (wasDragging && flushPreview) {
                flushDraggedLightPreview();
            }
        };

        return { handleStartDrag, handleEndDrag, draggingLight, transformControlsRef };
    };

    // Initialize available moods from appData
    React.useEffect(() => {
        if (appData.spaceData?.data?.moods) {
            const moods = appData.spaceData.data.moods.map((mood: any, index: number) => ({
                name: mood.name || `Mood ${index + 1}`,
                id: index.toString(),
                mood: mood
            }));
            setAvailableMoods(moods);

            // Prefer gallery mood index as the source of truth.
            const galleryMoodIndex = Number(appData.galleryData?.data?.mood);
            if (Number.isInteger(galleryMoodIndex) && galleryMoodIndex >= 0 && galleryMoodIndex < moods.length) {
                setSelectedMood(galleryMoodIndex.toString());
            } else if (currentMood) {
                // Fallback for older flows passing currentMood object only.
                const currentMoodIndex = appData.spaceData.data.moods.findIndex((m: any) => m.name === currentMood.name);
                if (currentMoodIndex !== -1) {
                    setSelectedMood(currentMoodIndex.toString());
                }
            }
        }
    }, [appData.spaceData?.data?.moods, currentMood]);

    // Get current settings based on selected mood
    const getCurrentSettings = (): MoodSettings => {
        if (!selectedMood || !appData.spaceData?.data?.moods) {
            return defaultSettings;
        }

        const selectedMoodData = appData.spaceData.data.moods[parseInt(selectedMood)];
        if (!selectedMoodData) {
            return defaultSettings;
        }

        // Ensure artworkMapIntensity has a default value if undefined
        if (selectedMoodData.renderSettings && selectedMoodData.renderSettings.artworkMapIntensity === undefined) {
            selectedMoodData.renderSettings.artworkMapIntensity = 1.0;
        }

        return normalizeMoodForPersistence(selectedMoodData);
    };

    const updateLocalMood = (newMood: MoodSettings) => {
        const localMood = {
            "published": newMood.published ?? false,
            "audienceType": newMood.audienceType ?? "general",
            "galleryId": newMood.galleryId ?? "",
            "galleryIds": Array.isArray(newMood.galleryIds) ? newMood.galleryIds : [],
            "users": Array.isArray(newMood.users) ? newMood.users : [],
            "name": newMood.name,
            "description": newMood.description,
            "thumbnailUrl": newMood.thumbnailUrl,
            "debugCameraEnabled": newMood.debugCameraEnabled ?? false,
            "cameraHeight": getMoodCameraHeight(newMood),
            "fog": {
                "enabled": newMood.fog.enabled,
                "near": newMood.fog.near,
                "far": newMood.fog.far,
                "color": newMood.fog.color,
                "applyFogToArtworks": newMood.fog.applyFogToArtworks
            },
            "lights": newMood.lights,
            "renderSettings": {
                "toneMapping": newMood.renderSettings.toneMapping,
                "toneMappingExposure": newMood.renderSettings.toneMappingExposure,
                "postProcessing": newMood.renderSettings.postProcessing,
                "artworkMapIntensity": newMood.renderSettings.artworkMapIntensity ?? 1.0
            },
            "sky": newMood.sky,
            "materialOverrides": newMood.materialOverrides,
            "lightMap": newMood.lightMap
        }

        return localMood
    }

    // Add new handlers for mood management
    const handleMoodSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const moodId = e.target.value;

        if (moodId) {
            const moodIndex = parseInt(moodId, 10);
            if (Number.isNaN(moodIndex) || moodIndex < 0) return;

            setSelectedMood(moodId);
            setRenderSettingsQueue(new Stack());
            const selectedMoodData = availableMoods[moodIndex]?.mood;
            if (!selectedMoodData) return;

            const currentDebugState = Boolean(appData.debugCameraEnabled);
            setDebugCameraEnabled(currentDebugState);
            appData.debugCameraEnabled = currentDebugState;
            const moodWithCurrentDebugState = {
                ...selectedMoodData,
                debugCameraEnabled: currentDebugState
            };

            setRuntimeCameraHeight(getMoodCameraHeight(selectedMoodData));

            settingsRef.current = moodWithCurrentDebugState;
            setSettings(moodWithCurrentDebugState);
            onChangeMood(moodIndex, updateLocalMood(moodWithCurrentDebugState));
        }
    };

    const handleAddNewMood = () => {
        // Reset all UI state
        setCollapsedSections({
            info: true,
            color: true,
            bloom: true,
            fog: true,
            postProcessing: true,
            lights: true,
            materialOverrides: true,
            sky: true,
            lightMap: true
        });
        setEditingLight(null);
        setEditingBloom(null);
        setEditingLUT(null);
        setEditingDotScreen(null);
        setEditingNoise(null);
        setDraggedItem(null);
        setDragOverIndex(null);
        setCurrentLightHelper(null);

        // Clean up any existing transform controls
        if (transformControlsRef.current) {
            transformControlsRef.current.detach();
            appData.spaceScene.remove(transformControlsRef.current);
            transformControlsRef.current = null;
        }

        // Reset file loading states
        setLoadingTextures(false);
        setLoadingLut(false);
        setLoadingHdri(false);
        setLoadingSkyImages(false);

        const isAdminMomSpace = appData.galleryData?.spaceType === "mom";
        const audienceType = isAdminMomSpace ? "general" : "gallery";
        const galleryAudienceId = audienceType === "gallery" ? String(appData.galleryData?._id || "") : "";
        const galleryAudienceIds = galleryAudienceId ? [galleryAudienceId] : [];
        const currentMoodSeed = getCurrentSettings();
        const clonedMood = JSON.parse(JSON.stringify(currentMoodSeed || defaultSettings));

        const newMood: MoodSettings = normalizeMoodForPersistence({
            ...clonedMood,
            published: false,
            audienceType,
            galleryId: galleryAudienceId,
            galleryIds: galleryAudienceIds,
            users: [],
            name: "New Mood",
            description: "",
            thumbnailUrl: "",
            debugCameraEnabled: false
        });

        // Add new mood to appData
        if (!appData.spaceData.data.moods) {
            appData.spaceData.data.moods = [];
        }
        appData.spaceData.data.moods.push(newMood);

        // Update available moods
        const newMoodId = (appData.spaceData.data.moods.length - 1).toString();
        setAvailableMoods(prev => [...prev, {
            name: "New Mood",
            id: newMoodId,
            mood: newMood
        }]);

        // Set the new mood as selected
        setSelectedMood(newMoodId);
        const newMoodIndex = Number.parseInt(newMoodId, 10);
        appData.galleryData.data.mood = newMoodIndex;
        onChangeMood(newMoodIndex, updateLocalMood(newMood));
    };

    const [settings, setSettings] = React.useState<MoodSettings>(defaultSettings);
    const settingsRef = React.useRef<MoodSettings>(defaultSettings);
    const hdrValidationLoaderRef = React.useRef<HDRLoader | null>(null);
    const livePreviewTimeoutRef = React.useRef<number | null>(null);
    const debugToggleLockRef = React.useRef(false);
    React.useEffect(() => {
        settingsRef.current = settings;
    }, [settings]);
    React.useEffect(() => {
        return () => {
            if (livePreviewTimeoutRef.current !== null) {
                window.clearTimeout(livePreviewTimeoutRef.current);
                livePreviewTimeoutRef.current = null;
            }
        };
    }, []);
    const [renderSettingsQueue, setRenderSettingsQueue] = React.useState<Stack>(new Stack());
    const addToRenderSettingsQueue = (item: MoodSettings) => {
        setRenderSettingsQueue((prevQueue) => new Stack([...prevQueue.getItems(), item]));
    }
    const queueMoodPreview = React.useCallback((moodIndex: number, moodPayload: any, immediate: boolean = false) => {
        if (livePreviewTimeoutRef.current !== null) {
            window.clearTimeout(livePreviewTimeoutRef.current);
            livePreviewTimeoutRef.current = null;
        }

        if (immediate) {
            onChangeMood(moodIndex, moodPayload);
            return;
        }

        // Coalesce rapid slider/input updates into fewer mood sync calls.
        livePreviewTimeoutRef.current = window.setTimeout(() => {
            onChangeMood(moodIndex, moodPayload);
            livePreviewTimeoutRef.current = null;
        }, 80);
    }, [onChangeMood]);
    const getSelectedMoodIndex = () => {
        if (!selectedMood) return null;

        const byIdIndex = availableMoods.findIndex((mood) => mood.id === selectedMood);
        if (byIdIndex !== -1) return byIdIndex;

        const parsed = Number.parseInt(selectedMood, 10);
        if (Number.isNaN(parsed) || parsed < 0 || parsed >= availableMoods.length) return null;
        return parsed;
    };

    const getEffectiveSelectedMoodIndex = () => {
        const galleryMood = Number.parseInt(String(appData.galleryData?.data?.mood), 10);
        if (Number.isInteger(galleryMood) && galleryMood >= 0 && galleryMood < availableMoods.length) {
            return galleryMood;
        }

        return getSelectedMoodIndex();
    };

    // Create a wrapper method for setSettings to apply additional functionality
    const updateSettings = (newSettingsOrUpdater: React.SetStateAction<MoodSettings>) => {
        let newSettings: MoodSettings;
        const previousSettings = settingsRef.current;

        // For function updaters, calculate the new state once
        if (typeof newSettingsOrUpdater === 'function') {
            const updater = newSettingsOrUpdater as (prev: MoodSettings) => MoodSettings;
            // Keep undo chain from the latest settings snapshot.
            addToRenderSettingsQueue(previousSettings);
            newSettings = updater(previousSettings);
        } else {
            // For direct object updates
            newSettings = newSettingsOrUpdater;
        }

        settingsRef.current = newSettings;
        // Update React state
        setSettings(newSettings);

        // During light drag, avoid re-applying mood every tick (it recreates lights and breaks gizmo dragging).
        if (isLightDragInProgressRef.current) {
            return;
        }

        // Propagate changes to the application state if a mood is selected
        const selectedMoodIndex = getSelectedMoodIndex();
        if (selectedMoodIndex !== null && availableMoods[selectedMoodIndex]) {
            const updatedLocalMoodData = updateLocalMood(newSettings);

            // Critical fix: Propagate to parent/manager
            // Pass true for 'local' parameter to indicate this is a live preview update
            queueMoodPreview(selectedMoodIndex, updatedLocalMoodData);
        } else {
            // Fallback for no selected mood
            updateLocalMood(newSettings);
        }
    };

    const undoSettings = () => {
        const newQueue = new Stack(renderSettingsQueue.getItems());

        const lastSettings = newQueue.pop();
        if (lastSettings) {
            settingsRef.current = lastSettings;
            setSettings(lastSettings);
            // Update local mood and propagate
            const updatedLocalMoodData = updateLocalMood(lastSettings);
            const selectedMoodIndex = getSelectedMoodIndex();
            if (selectedMoodIndex !== null && availableMoods[selectedMoodIndex]) {
                queueMoodPreview(selectedMoodIndex, updatedLocalMoodData, true);
            } else {
                updateLocalMood(lastSettings);
            }
        }
        setRenderSettingsQueue(newQueue);
    }

    // Reset state when modal is shown to ensure we get fresh data
    React.useEffect(() => {
        if (show) {
            // If modal is being shown, initialize with current mood or cached settings
            const initialSettings = getCurrentSettings();
            const initialDebugState = Boolean(appData.debugCameraEnabled);
            setDebugCameraEnabled(initialDebugState);
            appData.debugCameraEnabled = initialDebugState;
            updateSettings({
                ...initialSettings,
                debugCameraEnabled: initialDebugState
            });
        }
    }, [show]);

    // Update settings when selected mood changes
    React.useEffect(() => {
        if (selectedMood) {
            const selectedSettings = getCurrentSettings();
            const currentDebugState = Boolean(appData.debugCameraEnabled);
            setDebugCameraEnabled(currentDebugState);
            appData.debugCameraEnabled = currentDebugState;
            updateSettings({
                ...selectedSettings,
                debugCameraEnabled: currentDebugState
            });
        }
    }, [selectedMood]);

    const [collapsedSections, setCollapsedSections] = React.useState<{
        info: boolean;
        color: boolean;
        bloom: boolean;
        fog: boolean;
        postProcessing: boolean;
        lights: boolean;
        materialOverrides: boolean;
        sky: boolean;
        lightMap: boolean;
    }>({
        info: true,
        color: true,
        bloom: true,
        fog: true,
        postProcessing: true,
        lights: true,
        materialOverrides: true,
        sky: true,
        lightMap: true
    });

    const [editingLight, setEditingLight] = React.useState<number | null>(null);
    const [currentLightHelper, setCurrentLightHelper] = React.useState<THREE.DirectionalLightHelper | THREE.PointLightHelper | THREE.SpotLightHelper | null>(null);
    const flushDraggedLightPreview = () => {
        const fallbackMood = Number.parseInt(String(appData.galleryData?.data?.mood), 10);
        const moodIndex = getSelectedMoodIndex() ?? (Number.isInteger(fallbackMood) ? fallbackMood : 0);
        const moodPayload = updateLocalMood(settingsRef.current || settings);
        queueMoodPreview(moodIndex, moodPayload, true);
    };

    // Use the custom hook in the component
    const { handleStartDrag, handleEndDrag, draggingLight, transformControlsRef } = useTransformControls(appData, flushDraggedLightPreview);

    const [editingBloom, setEditingBloom] = React.useState<number | null>(null);
    const [editingLUT, setEditingLUT] = React.useState<number | null>(null);
    const [editingDotScreen, setEditingDotScreen] = React.useState<number | null>(null);
    const [editingNoise, setEditingNoise] = React.useState<number | null>(null);
    const [lutFiles, setLutFiles] = React.useState<string[]>([]);
    const [loadingLut, setLoadingLut] = React.useState<boolean>(false);
    const lutFileInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadingLut, setUploadingLut] = React.useState<boolean>(false);
    const visiblePostProcessingPasses = React.useMemo(
        () => getVisiblePostProcessingPasses(settings.renderSettings.postProcessing),
        [settings.renderSettings.postProcessing]
    );
    const createNewLightDefaultSettingsByType = (type: LightType) => {
        const lightsLenght = settings.lights.length;
        switch (type) {
            case "HemisphereLight":
                return {
                    type: type,
                    name: type + " " + lightsLenght,
                    intensity: 1,
                    skyColor: "#ffffff",
                    groundColor: "#ffffff"
                };
            case "AmbientLight":
                return {
                    type: type,
                    name: type + " " + lightsLenght,
                    intensity: 1,
                    color: "#ffffff"
                };
            case "DirectionalLight":
                return {
                    type: type,
                    name: type + " " + lightsLenght,
                    intensity: 1,
                    color: "#ffffff",
                    position: { x: 0, y: 5, z: 0 },
                    target: { x: 0, y: 0, z: 0 },
                    castShadow: false,
                    shadows: {
                        mapSize: 2048,
                        near: 0.5,
                        far: 200,
                        frustumLimit: 25,
                        bias: -0.0001,
                        normalBias: 0
                    }
                };
            case "PointLight":
                return {
                    type: type,
                    name: type + " " + lightsLenght,
                    intensity: 1,
                    color: "#ffffff",
                    position: { x: 0, y: 5, z: 0 },
                    distance: 10,
                    decay: 0.0
                };
            case "StrobeLight":
                return {
                    type,
                    name: "Strobe Light " + lightsLenght,
                    intensity: 1,
                    color: "#ffffff",
                    position: { x: 0, y: 5, z: 0 },
                    distance: 10,
                    decay: 0.0,
                    frequencyHz: DEFAULT_STROBE_FREQUENCY_HZ,
                };
            case "SpotLight":
                return {
                    type: type,
                    name: type + " " + lightsLenght,
                    intensity: 1,
                    color: "#ffffff",
                    position: { x: 0, y: 5, z: 0 },
                    target: { x: 0, y: 0, z: 0 },
                    distance: 10,
                    angle: Math.PI / 3,
                    penumbra: 0,
                    decay: 0.0,
                    castShadow: false,
                    shadows: {
                        mapSize: 2048,
                        near: 0.5,
                        far: 200,
                        frustumLimit: 25,
                        bias: -0.0001,
                        normalBias: 0
                    }
                };
        }
    }

    const toggleSection = (section: keyof typeof collapsedSections) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleExposureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newExposure = Number(e.target.value);
        updateSettings(prev => ({
            ...prev,
            renderSettings: {
                ...prev.renderSettings,
                toneMappingExposure: newExposure
            }
        }));
    };

    const handleToneMappingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        // Implementation of handleToneMappingChange
        updateSettings(prev => ({
            ...prev,
            renderSettings: {
                ...prev.renderSettings,
                toneMapping: Number(e.target.value)
            }
        }));
    };

    const toneMappingOptions = [
        { value: THREE.NoToneMapping, label: "No Tone Mapping" },
        { value: THREE.LinearToneMapping, label: "Linear Tone Mapping" },
        { value: THREE.ReinhardToneMapping, label: "Reinhard Tone Mapping" },
        { value: THREE.CineonToneMapping, label: "Cineon Tone Mapping" },
        { value: THREE.ACESFilmicToneMapping, label: "ACES Filmic Tone Mapping" },
        { value: (THREE as any).AgXToneMapping, label: "AgX Tone Mapping" },
        { value: (THREE as any).NeutralToneMapping, label: "Neutral Tone Mapping" },
        { value: (THREE as any).CustomToneMapping, label: "Custom Tone Mapping" }
    ].filter((option) => typeof option.value === "number");

    const handleArtworkMapIntensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newIntensity = Number(e.target.value);
        appData.renderService?.updateArtworkMapIntensity?.(newIntensity);
        updateSettings(prev => ({
            ...prev,
            renderSettings: {
                ...prev.renderSettings,
                artworkMapIntensity: newIntensity
            }
        }));
    };

    const handleBloomThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Implementation of handleBloomThresholdChange
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingBloom && pass.type === 'bloom'
                            ? { ...pass, threshold: Number(e.target.value) }
                            : pass
                    )
                }
            };
            return newSettings;
        });

    };

    const handleBloomStrengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Implementation of handleBloomStrengthChange
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingBloom && pass.type === 'bloom'
                            ? { ...pass, strength: Number(e.target.value) }
                            : pass
                    )
                }
            };
            return newSettings;
        });
    };

    const handleBloomRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Implementation of handleBloomRadiusChange
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingBloom && pass.type === 'bloom'
                            ? { ...pass, radius: Number(e.target.value) }
                            : pass
                    )
                }
            };
            return newSettings;
        });
    };

    const handleFogNearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Implementation of handleFogNearChange
        updateSettings(prev => ({
            ...prev,
            fog: {
                ...prev.fog,
                near: Number(e.target.value)
            }
        }));
    };

    const handleFogFarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Implementation of handleFogFarChange
        updateSettings(prev => ({
            ...prev,
            fog: {
                ...prev.fog,
                far: Number(e.target.value)
            }
        }));
    };

    const handleFogColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Implementation of handleFogColorChange
        updateSettings(prev => ({
            ...prev,
            fog: {
                ...prev.fog,
                color: e.target.value
            }
        }));
    };

    const handleFogApplyToArtworksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                fog: {
                    ...prev.fog,
                    applyFogToArtworks: e.target.checked
                }
            };
            return newSettings;
        });
    };

    const handleAddPass = (passType: string) => {
        if (!settings.renderSettings.postProcessing.some(pass => pass.type === passType)) {
            const newPass = (() => {
                switch (passType) {
                    case 'render':
                        return { type: "render" } as RenderPass;
                    case 'fxaa':
                        return { type: "fxaa" } as FXAAPass;
                    case 'bloom':
                        return {
                            type: "bloom",
                            threshold: 0.75,
                            strength: 1.0,
                            radius: 0.5
                        } as BloomPass;
                    case 'lut':
                        return {
                            type: "lut",
                            cubeMapUrl: lutFiles.length > 0 ? lutFiles[0] : '',
                            intensity: 1.0
                        } as LUTPass;
                    case 'dotScreen':
                        return {
                            type: "dotScreen",
                            scale: 1.0,
                            angle: 1.57
                        } as DotScreenPass;
                    case 'noise':
                        return {
                            type: "noise",
                            amount: 0.1
                        } as NoisePass;
                    case 'glitch':
                        return { type: "glitch" } as GlitchPass;
                    default:
                        return { type: "render" } as RenderPass;
                }
            })();

            updateSettings(prev => {
                const newSettings = {
                    ...prev,
                    renderSettings: {
                        ...prev.renderSettings,
                        postProcessing: [...prev.renderSettings.postProcessing, newPass]
                    }
                };
                return newSettings;
            });
        }
    };

    const handleRemovePass = (index: number) => {
        const reindexAfterRemoval = (currentIndex: number | null) => {
            if (currentIndex === null) return null;
            if (currentIndex === index) return null;
            if (currentIndex > index) return currentIndex - 1;
            return currentIndex;
        };
        setEditingBloom((prev) => reindexAfterRemoval(prev));
        setEditingLUT((prev) => reindexAfterRemoval(prev));
        setEditingDotScreen((prev) => reindexAfterRemoval(prev));
        setEditingNoise((prev) => reindexAfterRemoval(prev));

        updateSettings(prev => {
            const filteredPasses = prev.renderSettings.postProcessing.filter((_, i) => i !== index);
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: ensureRenderPass(filteredPasses)
                }
            };
            return newSettings;
        });
    };

    const handleFogEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSettings(prev => ({
            ...prev,
            fog: {
                ...prev.fog,
                enabled: e.target.checked
            }
        }));
    };

    function getVisiblePostProcessingPasses(postProcessing: any[]) {
        return postProcessing
            .map((pass, sourceIndex) => ({ pass, sourceIndex }))
            .filter(({ pass }) => pass?.type && pass.type !== "renderArtwork");
    }

    const remapIndexAfterMove = (currentIndex: number | null, fromIndex: number, toIndex: number): number | null => {
        if (currentIndex === null) return null;
        if (currentIndex === fromIndex) return toIndex;
        if (fromIndex < toIndex && currentIndex > fromIndex && currentIndex <= toIndex) return currentIndex - 1;
        if (fromIndex > toIndex && currentIndex >= toIndex && currentIndex < fromIndex) return currentIndex + 1;
        return currentIndex;
    };

    const handleDragStart = (index: number) => {
        setDraggedItem(index);
    };

    const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault(); // Necessary to allow dropping
    };

    const handleDragEnterDropZone = (index: number) => {
        setDragOverIndex(index);
    };

    const handleDragLeaveDropZone = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        if (draggedItem === null) return;

        const currentPostProcessing = settings.renderSettings.postProcessing;
        const visiblePasses = getVisiblePostProcessingPasses(currentPostProcessing);
        const draggedVisibleIndex = visiblePasses.findIndex(({ sourceIndex }) => sourceIndex === draggedItem);
        if (draggedVisibleIndex === -1) {
            setDraggedItem(null);
            setDragOverIndex(null);
            return;
        }

        // Skip if trying to drop in the same visible position or directly after it.
        if (targetIndex === draggedVisibleIndex || targetIndex === draggedVisibleIndex + 1) {
            setDraggedItem(null);
            setDragOverIndex(null);
            return;
        }

        const firstVisibleSourceIndex = visiblePasses[0]?.sourceIndex ?? 0;
        const lastVisibleSourceIndex = visiblePasses[visiblePasses.length - 1]?.sourceIndex ?? (currentPostProcessing.length - 1);
        let insertionIndex = firstVisibleSourceIndex;
        if (targetIndex <= 0) {
            insertionIndex = firstVisibleSourceIndex;
        } else if (targetIndex >= visiblePasses.length) {
            insertionIndex = lastVisibleSourceIndex + 1;
        } else {
            insertionIndex = visiblePasses[targetIndex].sourceIndex;
        }

        const movedFromIndex = draggedItem;
        const movedToIndex = movedFromIndex < insertionIndex ? insertionIndex - 1 : insertionIndex;

        setEditingLUT((prev) => remapIndexAfterMove(prev, movedFromIndex, movedToIndex));
        setEditingBloom((prev) => remapIndexAfterMove(prev, movedFromIndex, movedToIndex));
        setEditingDotScreen((prev) => remapIndexAfterMove(prev, movedFromIndex, movedToIndex));
        setEditingNoise((prev) => remapIndexAfterMove(prev, movedFromIndex, movedToIndex));

        updateSettings(prev => {
            const newPassOrder = [...prev.renderSettings.postProcessing];
            const [removed] = newPassOrder.splice(movedFromIndex, 1);
            newPassOrder.splice(movedToIndex, 0, removed);
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: newPassOrder
                }
            };
            return newSettings;
        });
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    const handleAddLight = (type: LightType) => {
        // Implementation of handleAddLight
        const newLightProps = createNewLightDefaultSettingsByType(type);
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                lights: [...prev.lights, newLightProps]
            };
            return newSettings;
        });

        utils.loadLight(newLightProps, appData);
        appData.renderService.addToRenderQueue();
    };

    const handleRemoveLight = (index: number) => {
        // If any drag session is active, stop it before mutating lights.
        // Otherwise TransformControls can stay attached to a removed object.
        if (draggingLight) {
            handleEndDrag(false);
        }

        // Clean up light helper UI state before removing the backing light.
        if (currentLightHelper) {
            if (currentLightHelper.parent) {
                currentLightHelper.parent.remove(currentLightHelper);
            } else {
                appData.spaceScene.remove(currentLightHelper);
            }
            setCurrentLightHelper(null);
        }

        setEditingLight((prev) => {
            if (prev === null) return null;
            if (prev === index) return null;
            return prev > index ? prev - 1 : prev;
        });

        updateSettings(prev => ({
            ...prev,
            lights: prev.lights.filter((_, i) => i !== index)
        }));

        utils.removeLight(index, appData);
        appData.renderService.addToRenderQueue();
    };

    const handleLightChange = (index: number, field: keyof LightSettings, value: any) => {
        // Implementation of handleLightChange
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                lights: prev.lights.map((light, i) =>
                    i === index ? { ...light, [field]: value } : light
                )
            };

            // Update light helper if one exists
            if (currentLightHelper && editingLight === index) {
                if (currentLightHelper instanceof THREE.DirectionalLightHelper ||
                    currentLightHelper instanceof THREE.PointLightHelper ||
                    currentLightHelper instanceof THREE.SpotLightHelper) {
                    currentLightHelper.update();
                }
            }

            return newSettings;
        });
    };

    const handlePositionChange = (index: number, axis: 'x' | 'y' | 'z', value: number) => {
        // Implementation of handlePositionChange
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                lights: prev.lights.map((light, i) =>
                    i === index && light.position
                        ? {
                            ...light,
                            position: {
                                ...light.position,
                                [axis]: value
                            }
                        }
                        : light
                )
            };
            // Update light helper if one exists and we're editing this light
            if (currentLightHelper && editingLight === index) {
                if (currentLightHelper instanceof THREE.DirectionalLightHelper ||
                    currentLightHelper instanceof THREE.PointLightHelper ||
                    currentLightHelper instanceof THREE.SpotLightHelper) {
                    currentLightHelper.update();
                }
            }
            return newSettings;
        });


    };

    const handleTargetChange = (index: number, axis: 'x' | 'y' | 'z', value: number) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                lights: prev.lights.map((light, i) =>
                    i === index && light.target
                        ? {
                            ...light,
                            target: {
                                ...light.target,
                                [axis]: value
                            }
                        }
                        : light
                )
            };

            if (currentLightHelper && editingLight === index) {
                if (currentLightHelper instanceof THREE.DirectionalLightHelper ||
                    currentLightHelper instanceof THREE.SpotLightHelper) {
                    currentLightHelper.update();
                }
            }

            return newSettings;
        });
    };

    const handleShadowChange = (index: number, field: keyof LightSettings['shadows'], value: number) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                lights: prev.lights.map((light, i) =>
                    i === index ? {
                        ...light,
                        shadows: {
                            ...light.shadows,
                            [field]: value
                        }
                    } : light
                )
            };
            return newSettings;
        });
    };

    const handleAddMaterialOverride = (type: MaterialOverrideType) => {
        const newOverride: MaterialOverride = {
            textureUrl: '',
            meshName: '',
            channel: type, // Set target to the same value as type
            repeat: 1
        };
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                materialOverrides: [...prev.materialOverrides, newOverride]
            };
            return newSettings;
        });
    };

    const handleRemoveMaterialOverride = (index: number) => {
        updateSettings(prev => {
            // Then create new settings with the filtered array
            const newSettings = {
                ...prev,
                materialOverrides: prev.materialOverrides.filter((_, i) => i !== index)
            };
            return newSettings;
        });
    };

    const handleMaterialOverrideChange = (index: number, field: keyof MaterialOverride, value: any) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                materialOverrides: prev.materialOverrides.map((override, i) =>
                    i === index ? { ...override, [field]: value } : override
                )
            };
            return newSettings;
        });
    };

    const handleSkyChange = async (field: keyof SkySettings, value: any) => {
        if (field === "hdriUrl") {
            const nextUrl = String(value || "");
            if (nextUrl) {
                const validateHDR = async (url: string): Promise<boolean> => {
                    try {
                        if (!hdrValidationLoaderRef.current) {
                            hdrValidationLoaderRef.current = new HDRLoader();
                            hdrValidationLoaderRef.current.setDataType(THREE.HalfFloatType);
                        }
                        const response = await fetch(url, { cache: "no-store" });
                        if (!response.ok) return false;
                        const arrayBuffer = await response.arrayBuffer();
                        const parsed: any = hdrValidationLoaderRef.current.parse(arrayBuffer);
                        return Boolean(parsed && (parsed.image || parsed.data));
                    } catch {
                        return false;
                    }
                };

                const isValidHDR = await validateHDR(nextUrl);
                if (!isValidHDR) {
                    const savedMoodIndex = getSelectedMoodIndex();
                    const fallbackHdriUrl = (
                        savedMoodIndex !== null &&
                        appData.spaceData?.data?.moods?.[savedMoodIndex]?.sky?.hdriUrl
                    ) ? String(appData.spaceData.data.moods[savedMoodIndex].sky.hdriUrl) : "";

                    updateSettings(prev => ({
                        ...prev,
                        sky: {
                            ...prev.sky,
                            hdriUrl: fallbackHdriUrl
                        }
                    }));

                    failedNotify("Invalid HDRI file format. Reverted to saved HDRI.");
                    return;
                }
            }
        }

        updateSettings(prev => {
            const newSettings = {
                ...prev,
                sky: {
                    ...prev.sky,
                    [field]: value
                }
            };
            return newSettings;
        });
    };

    const handleInfoChange = (field: keyof InfoSettings, value: string) => {
        // Implementation of handleInfoChange
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                [field]: value
            };
            return newSettings;
        });
    };

    const handleApply = async () => {
        // Remove any light helper before closing
        if (currentLightHelper) {
            appData.spaceScene.remove(currentLightHelper);
            setCurrentLightHelper(null);
            appData.renderService.addToRenderQueue();
        }

        const moodIndex = getEffectiveSelectedMoodIndex();
        if (moodIndex === null || !availableMoods[moodIndex]) return;

        const persistedSettings = normalizeMoodForPersistence(settings);
        // Bird's-eye / debug camera is runtime-only and should never be persisted.
        persistedSettings.debugCameraEnabled = false;

        await onApplyMood(moodIndex, persistedSettings);
        setSettings(persistedSettings);
        settingsRef.current = persistedSettings;
        setSelectedMood(availableMoods[moodIndex].id);
        setAvailableMoods(prev =>
            prev.map((mood, index) => (index === moodIndex ? { ...mood, mood: persistedSettings } : mood))
        );
    };

    // Update handleDebugCameraToggle to use transformControlsRef
    const handleDebugCameraToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isEnabled = e.target.checked;
        if (debugToggleLockRef.current) {
            return;
        }
        debugToggleLockRef.current = true;
        window.setTimeout(() => {
            debugToggleLockRef.current = false;
        }, 180);
        toggleDebugCamera(isEnabled);
    };

    const toggleDebugCamera = (enabled: boolean) => {
        setDebugCameraEnabled(enabled);
        appData.debugCameraEnabled = enabled;
        setRuntimeFov(getActiveRuntimeFov(appData, enabled));
        const baseSettings = settingsRef.current || settings;
        const nextSettings = {
            ...baseSettings,
            debugCameraEnabled: enabled
        };
        settingsRef.current = nextSettings;
        setSettings(nextSettings);

        handleEndDrag(false);

        const payloadMood = updateLocalMood(nextSettings);
        const moodIndex = getSelectedMoodIndex() ?? appData.galleryData?.data?.mood ?? 0;
        onChangeMood(moodIndex, {
            ...payloadMood,
            debugCameraEnabled: enabled
        });
        appData.renderService?.addToRenderQueue?.(30);
    }

    React.useEffect(() => {
        if (!show) {
            return;
        }

        setRuntimeFov(getActiveRuntimeFov(appData, debugCameraEnabled));
        setRuntimeCameraHeight(getCurrentRuntimeCameraHeight(appData));
    }, [show, appData, debugCameraEnabled]);

    const handleRuntimeFovChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextFov = applyRuntimeFov(appData, debugCameraEnabled, Number(e.target.value));
        setRuntimeFov(nextFov);
    };

    const handleRuntimeCameraHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextHeight = applyRuntimeCameraHeight(appData, Number(e.target.value));
        setRuntimeCameraHeight(nextHeight);
        updateSettings(prev => ({
            ...prev,
            cameraHeight: nextHeight
        }));
    };

    const [textureFiles, setTextureFiles] = React.useState<string[]>([]);
    const [loadingTextures, setLoadingTextures] = React.useState<boolean>(false);
    const textureFileInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadingTexture, setUploadingTexture] = React.useState<boolean>(false);
    const [hdriFiles, setHdriFiles] = React.useState<string[]>([]);
    const [loadingHdri, setLoadingHdri] = React.useState<boolean>(false);
    const hdriFileInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadingHdri, setUploadingHdri] = React.useState<boolean>(false);
    const [skyImageFiles, setSkyImageFiles] = React.useState<string[]>([]);
    const [loadingSkyImages, setLoadingSkyImages] = React.useState<boolean>(false);
    const skyImageFileInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadingSkyImage, setUploadingSkyImage] = React.useState<boolean>(false);
    const [loadingLightMaps, setLoadingLightMaps] = React.useState<boolean>(false);
    const [lightMapFiles, setLightMapFiles] = React.useState<string[]>([]);
    const [uploadingLightMap, setUploadingLightMap] = React.useState<boolean>(false);
    const lightMapFileInputRef = React.useRef<HTMLInputElement>(null);

    // Add this near other useEffect hooks
    React.useEffect(() => {
        (async () => {
            if (!collapsedSections.lightMap && lightMapFiles.length === 0) {
                setLoadingLightMaps(true);
                try {
                    const files = await onGetFilesFromFolder("mood/light-map/")
                    const imageFiles = files.filter(file => {
                        const lowerFile = file.toLowerCase();
                        return lowerFile.endsWith('.jpg') || lowerFile.endsWith('.jpeg') || lowerFile.endsWith('.png');
                    });
                    setLightMapFiles(imageFiles);
                } catch (error) {
                    // show error message 
                    failedNotify('Failed to load light map files')
                } finally {
                    setLoadingLightMaps(false);
                }
            }
        })();
    }, [collapsedSections.lightMap, onGetFilesFromFolder]);

    const handleUploadLightMapFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onUploadFilesToFolder && e.target.files && e.target.files.length > 0) {
            setUploadingLightMap(true);
            try {
                const files = Array.from(e.target.files);
                const uploadedUrls = await onUploadFilesToFolder('mood/light-map', files);
                setLightMapFiles(prev => [...prev, ...uploadedUrls.map(url => url.url)]);
            } catch (error) {
                failedNotify('Failed to upload light map files')
                console.error("Error uploading light map files:", error);
            } finally {
                setUploadingLightMap(false);
                if (lightMapFileInputRef.current) {
                    lightMapFileInputRef.current.value = '';
                }
            }
        }
    };

    const handleUploadTextureFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onUploadFilesToFolder && e.target.files && e.target.files.length > 0) {
            setUploadingTexture(true);
            try {
                const files = Array.from(e.target.files);
                const uploadedUrls = await onUploadFilesToFolder('mood/material-textures', files);

                // Add the new textures to the list
                setTextureFiles(prev => [...prev, ...uploadedUrls.map(url => url.url)]);

                // If we're editing a material override, automatically select the first uploaded texture
                if (uploadedUrls.length > 0) {
                    // Don't automatically apply; let the user choose from the dropdown
                    toast.success(`Uploaded ${uploadedUrls.length} texture${uploadedUrls.length > 1 ? 's' : ''} successfully`);
                }
            } catch (error) {
                console.error("Error uploading texture files:", error);
                failedNotify("Failed to upload texture files");
            } finally {
                setUploadingTexture(false);
                if (textureFileInputRef.current) {
                    textureFileInputRef.current.value = '';
                }
            }
        }
    };

    React.useEffect(() => {
        (async () => {
            // Load texture files when the material overrides section is opened
            if (!collapsedSections.materialOverrides && textureFiles.length === 0) {
                setLoadingTextures(true);
                try {
                    const files = await onGetFilesFromFolder("mood/material-textures")
                    setTextureFiles(files);
                } catch (error) {
                    console.error("Error loading texture files:", error);
                    failedNotify('Failed to load material textures')
                } finally {
                    setLoadingTextures(false);
                }
            }
        })();
    }, [collapsedSections.materialOverrides, onGetFilesFromFolder]);

    // Load HDRI files when the sky section is opened
    React.useEffect(() => {
        (async () => {
            if (!collapsedSections.sky && hdriFiles.length === 0) {
                setLoadingHdri(true);
                setLoadingSkyImages(true);

                try {
                    const skyHdriFiles = await onGetFilesFromFolder("mood/sky/hdri/")
                    const hdriFiles = skyHdriFiles.filter(file => file.toLowerCase().endsWith('.hdr'));
                    setHdriFiles(hdriFiles);

                    const skyImageFiles = await onGetFilesFromFolder("mood/sky/images/")
                    const imageFiles = skyImageFiles.filter(file => {
                        const lowerFile = file.toLowerCase();
                        return lowerFile.endsWith('.jpg') || lowerFile.endsWith('.jpeg') || lowerFile.endsWith('.png');
                    });
                    setSkyImageFiles(prev => [...prev, ...imageFiles]);
                } catch (error) {
                    failedNotify('Failed to load sky files')
                } finally {
                    setLoadingHdri(false);
                    setLoadingSkyImages(false);
                }
            }
        })();
    }, [collapsedSections.sky, onGetFilesFromFolder]);

    const handleUploadSkyHdriFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onUploadFilesToFolder && e.target.files && e.target.files.length > 0) {
            setUploadingHdri(true);
            try {
                const files = Array.from(e.target.files);
                let uploadedUrls = await onUploadFilesToFolder('mood/sky/hdri', files);

                // Add the new HDRI files to the list
                setHdriFiles(prev => [...prev, ...uploadedUrls.map(url => url.url)]);

                // If we're editing a sky, automatically select the first uploaded HDRI
                if (uploadedUrls.length > 0 && !settings.sky.hdriUrl) {
                    await handleSkyChange('hdriUrl', uploadedUrls[0].url);
                }

                toast.success(`Uploaded ${uploadedUrls.length} HDRI file${uploadedUrls.length > 1 ? 's' : ''} successfully`);
            } catch (error) {
                console.error("Error uploading HDRI files:", error);
                toast.error("Failed to upload HDRI files");
            } finally {
                setUploadingHdri(false);
                if (hdriFileInputRef.current) {
                    hdriFileInputRef.current.value = '';
                }
            }
        }
    };

    const handleUploadSkyImageFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onUploadFilesToFolder && e.target.files && e.target.files.length > 0) {
            setUploadingSkyImage(true);
            try {
                const files = Array.from(e.target.files);
                const uploadedUrls = await onUploadFilesToFolder('mood/sky/images', files);

                // Add the new sky image files to the list
                setSkyImageFiles(prev => [...prev, ...uploadedUrls.map(url => url.url)]);

                // If we're editing a sky, automatically select the first uploaded image
                if (uploadedUrls.length > 0 && !settings.sky.skyImageUrl) {
                    await handleSkyChange('skyImageUrl', uploadedUrls[0].url);
                }

                toast.success(`Uploaded ${uploadedUrls.length} sky image${uploadedUrls.length > 1 ? 's' : ''} successfully`);
            } catch (error) {
                console.error("Error uploading sky image files:", error);
                toast.error("Failed to upload sky image files");
            } finally {
                setUploadingSkyImage(false);
                if (skyImageFileInputRef.current) {
                    skyImageFileInputRef.current.value = '';
                }
            }
        }
    };

    // Function to handle LUT intensity change
    const handleLUTIntensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingLUT && pass.type === 'lut'
                            ? { ...pass, intensity: Number(e.target.value) }
                            : pass
                    )
                }
            };
            appData.renderService.updateLUT(newSettings.renderSettings.postProcessing[editingLUT]);
            return newSettings;
        });
    };

    // Function to handle LUT file selection
    const handleLUTFileSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingLUT && pass.type === 'lut'
                            ? { ...pass, cubeMapUrl: e.target.value }
                            : pass
                    )
                }
            };
            appData.renderService.updateLUT(newSettings.renderSettings.postProcessing[editingLUT]);
            return newSettings;
        });
    };

    // Function to handle Dot Screen scale change
    const handleDotScreenScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingDotScreen && pass.type === 'dotScreen'
                            ? { ...pass, scale: Number(e.target.value) }
                            : pass
                    )
                }
            };
            return newSettings;
        });
    };

    // Function to handle Dot Screen angle change
    const handleDotScreenAngleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingDotScreen && pass.type === 'dotScreen'
                            ? { ...pass, angle: Number(e.target.value) }
                            : pass
                    )
                }
            };
            return newSettings;
        });
    };

    // Function to handle Noise amount change
    const handleNoiseAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                renderSettings: {
                    ...prev.renderSettings,
                    postProcessing: prev.renderSettings.postProcessing.map((pass, i) =>
                        i === editingNoise && pass.type === 'noise'
                            ? { ...pass, amount: Number(e.target.value) }
                            : pass
                    )
                }
            };
            return newSettings;
        });
    };

    // Load LUT files when post-processing section is opened
    React.useEffect(() => {
        (async () => {
            if (!collapsedSections.postProcessing && lutFiles.length === 0) {
                setLoadingLut(true);

                try {
                    const files = await onGetFilesFromFolder('mood/lut-cube/')
                    const lutFiles = files.filter(file => file.toLowerCase().endsWith('.cube'));
                    setLutFiles(lutFiles);
                } catch (error) {
                    console.error("Error loading LUT files:", error);
                    failedNotify('Failed to load LUT files')
                } finally {
                    setLoadingLut(false);
                }
            }
        })();
    }, [collapsedSections.postProcessing, onGetFilesFromFolder]);

    const handleHDRIntensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newIntensity = Number(e.target.value);
        updateSettings(prev => {
            const newSettings = {
                ...prev,
                sky: {
                    ...prev.sky,
                    hdrIntensity: newIntensity
                }
            };
            return newSettings;
        });
    };

    /*
    Legacy publish action kept as requested:
    const onPublishMood = async () => {
        // Make sure current settings are saved
        const moodIndex = availableMoods.findIndex(mood => mood.id === selectedMood);
        if (moodIndex !== -1) {
            const publishedSettings = {
                ...settings,
                published: !settings.published
            }
            await onApplyMood(moodIndex, publishedSettings);
            setSettings(publishedSettings);
            setAvailableMoods(prev => prev.map(mood => mood.id === selectedMood ? { ...mood, mood: publishedSettings } : mood));
        }
    }
    */

    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

    // Add this near other useEffect hooks
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.getElementById('mesh-dropdown');
            if (dropdown && !dropdown.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div
            className={`position-fixed top-0 start-0 h-100 ${show ? 'd-block' : 'd-none'}`}
            style={{
                width: '500px',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                zIndex: 1000,
                overflowY: 'auto',
                pointerEvents: 'auto'
            }}
        >
            <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="m-0">Render Settings</h4>
                    <Button onClick={() => {
                        // Remove any light helper before closing
                        if (currentLightHelper) {
                            appData.spaceScene.remove(currentLightHelper);
                            setCurrentLightHelper(null);
                            appData.renderService.addToRenderQueue();
                        }

                        if (debugCameraEnabled) {
                            toggleDebugCamera(false);
                        }

                        onHide();
                    }} className="btn-sm">Close</Button>
                </div>

                <div className="container-fluid p-0">
                    {/* Moods Section - Always visible */}
                    <div className="mb-2 p-3 bg-dark rounded">
                        <h5 className="mb-3">Moods</h5>
                        <div className="d-flex gap-2">
                            <select
                                className="form-select"
                                value={selectedMood}
                                onChange={handleMoodSelect}
                            >
                                <option value="">Select a mood...</option>
                                {availableMoods.map((mood) => (
                                    <option key={mood.id} value={mood.id}>
                                        {mood.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                className="btn btn-primary"
                                onClick={handleAddNewMood}
                            >
                                New Mood
                            </button>
                        </div>
                        <h5 className="mb-3 mt-4">Bird Eye Camera</h5>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="debugCameraSwitch"
                                checked={debugCameraEnabled}
                                onChange={handleDebugCameraToggle}
                            />
                            <label className="form-check-label" htmlFor="debugCameraSwitch">
                                Bird Eye Camera (Fog will be disabled)
                            </label>
                        </div>
                        <div className="mt-3">
                            <label className="form-label" htmlFor="runtimeFovRange">Camera FOV</label>
                            <input
                                id="runtimeFovRange"
                                type="range"
                                className="form-range"
                                min={RUNTIME_FOV_MIN}
                                max={RUNTIME_FOV_MAX}
                                step="1"
                                value={runtimeFov}
                                onChange={handleRuntimeFovChange}
                            />
                            <div className="text-center">{runtimeFov.toFixed(0)}</div>
                        </div>
                        <div className="mt-3">
                            <label className="form-label" htmlFor="runtimeCameraHeightRange">Camera Height</label>
                            <input
                                id="runtimeCameraHeightRange"
                                type="range"
                                className="form-range"
                                min={RUNTIME_CAMERA_HEIGHT_MIN}
                                max={RUNTIME_CAMERA_HEIGHT_MAX}
                                step={RUNTIME_CAMERA_HEIGHT_STEP}
                                value={runtimeCameraHeight}
                                onChange={handleRuntimeCameraHeightChange}
                            />
                            <div className="text-center">{runtimeCameraHeight.toFixed(2)}</div>
                        </div>
                    </div>


                    {/* Info */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('info')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.info ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Info</h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.info ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <label className="form-label">Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={settings.name}
                                        onChange={(e) => handleInfoChange('name', e.target.value)}
                                        placeholder="Enter name"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        value={settings.description}
                                        onChange={(e) => handleInfoChange('description', e.target.value)}
                                        placeholder="Enter description"
                                        rows={3}
                                    />
                                </div>
                                <div className="mb-3 form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="publishMood"
                                        checked={settings.published ?? false}
                                        onChange={(e) => updateSettings(prev => ({ ...prev, published: e.target.checked }))}
                                    />
                                    <label className="form-check-label" htmlFor="publishMood">
                                        Publish (Apply to all galleries)
                                    </label>
                                </div>
                                {/* <div className="mb-3">
                                    <label className="form-label">Thumbnail URL</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={settings.info.thumbnailUrl}
                                        onChange={(e) => handleInfoChange('thumbnailUrl', e.target.value)}
                                        placeholder="Enter thumbnail URL"
                                    />
                                    {settings.info.thumbnailUrl && (
                                        <div className="mt-2">
                                            <img
                                                src={settings.info.thumbnailUrl}
                                                alt="Thumbnail preview"
                                                className="img-thumbnail"
                                                style={{ maxWidth: '100%', maxHeight: '150px' }}
                                            />
                                        </div>
                                    )}
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Color */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('color')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.color ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Color Settings</h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.color ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <label className="form-label">Tone Mapping</label>
                                    <select
                                        className="form-select"
                                        value={settings.renderSettings.toneMapping}
                                        onChange={handleToneMappingChange}
                                    >
                                        {toneMappingOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {settings.renderSettings.toneMapping !== THREE.NoToneMapping && (
                                    <div className="mb-3">
                                        <label className="form-label">Exposure</label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="0.01"
                                            max="5.0"
                                            step="0.0001"
                                            value={settings.renderSettings.toneMappingExposure}
                                            onChange={handleExposureChange}
                                        />
                                        <div className="text-center">{settings.renderSettings.toneMappingExposure.toFixed(2)}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Fog */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('fog')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.fog ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Fog Settings</h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.fog ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={settings.fog.enabled}
                                            onChange={handleFogEnabledChange}
                                            id="fogEnabled"
                                            disabled={debugCameraEnabled}
                                        />
                                        <label className="form-check-label" htmlFor="fogEnabled">
                                            Enable Fog
                                        </label>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Near Distance</label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="00.0"
                                            max="100.0"
                                            step="0.01"
                                            value={settings.fog.near}
                                            disabled={debugCameraEnabled}
                                            onChange={handleFogNearChange}
                                        />
                                        <div className="text-center">{settings.fog.near.toFixed(2)}</div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Far Distance</label>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min="0.0"
                                            max="300.0"
                                            step="0.01"
                                            value={settings.fog.far}
                                            disabled={debugCameraEnabled}
                                            onChange={handleFogFarChange}
                                        />
                                        <div className="text-center">{settings.fog.far.toFixed(2)}</div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Color</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={settings.fog.color}
                                                onChange={handleFogColorChange}
                                                title="Choose fog color"
                                                disabled={debugCameraEnabled}
                                            />
                                            <span>{settings.fog.color}</span>
                                        </div>
                                    </div>
                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={settings.fog.applyFogToArtworks}
                                            onChange={handleFogApplyToArtworksChange}
                                            id="fogApplyToArtworks"
                                        />
                                        <label className="form-check-label" htmlFor="fogApplyToArtworks">
                                            Apply Fog to Artworks
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lights */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('lights')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.lights ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Lights</h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.lights ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <div className="d-flex flex-wrap gap-2">
                                        {[
                                            { type: "HemisphereLight", label: "HemisphereLight" },
                                            { type: "AmbientLight", label: "AmbientLight" },
                                            { type: "DirectionalLight", label: "DirectionalLight" },
                                            { type: "PointLight", label: "PointLight" },
                                            { type: "SpotLight", label: "SpotLight" },
                                            { type: "StrobeLight", label: "Strobe Light" },
                                        ].map(({ type, label }) => (
                                            <button
                                                key={type}
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleAddLight(type as LightType)}
                                            >
                                                Add {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="border rounded p-2">
                                    {settings.lights.map((light, index) => (
                                        <div key={index} className="mb-3 p-2 bg-dark rounded">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="m-0">{light.name}</h6>
                                                <div>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary me-2"
                                                        onClick={() => {
                                                            if (editingLight === index) {
                                                                // Correctly use handleEndDrag to reset dragging state
                                                                handleEndDrag();

                                                                // Remove light helper when done
                                                                if (currentLightHelper) {
                                                                    appData.spaceScene.remove(currentLightHelper);
                                                                    setCurrentLightHelper(null);
                                                                    appData.renderService.addToRenderQueue();
                                                                }
                                                            } else {
                                                                // Add appropriate light helper
                                                                const light = getLightByIndex(appData, index);
                                                                if (light) {
                                                                    // Remove any existing helper first
                                                                    if (currentLightHelper) {
                                                                        appData.spaceScene.remove(currentLightHelper);
                                                                    }

                                                                    let helper;
                                                                    if (light.type === 'DirectionalLight') {
                                                                        helper = new THREE.DirectionalLightHelper(light, 1);
                                                                    } else if (light.type === 'PointLight') {
                                                                        helper = new THREE.PointLightHelper(light, 0.5);
                                                                    } else if (light.type === 'SpotLight') {
                                                                        helper = new THREE.SpotLightHelper(light);
                                                                    }

                                                                    if (helper) {
                                                                        const lightHelper = appData.spaceScene.getObjectByName("lightHelper")
                                                                        lightHelper.add(helper);
                                                                        setCurrentLightHelper(helper);
                                                                        appData.renderService.addToRenderQueue();
                                                                    }
                                                                }
                                                            }
                                                            setEditingLight(editingLight === index ? null : index);
                                                        }}
                                                    >
                                                        {editingLight === index ? 'Done' : 'Edit'}
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleRemoveLight(index)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                            {editingLight === index && (
                                                <div className="mt-2">
                                                    <div className="mb-2">
                                                        <label className="form-label">Name</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={light.name}
                                                            onChange={(e) => handleLightChange(index, 'name', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="mb-2">
                                                        <label className="form-label">Intensity</label>
                                                        <input
                                                            type="range"
                                                            className="form-range"
                                                            min="0"
                                                            max="10"
                                                            step="0.01"
                                                            value={light.intensity}
                                                            onChange={(e) => handleLightChange(index, 'intensity', Number(e.target.value))}
                                                        />
                                                        <div className="text-center">{light.intensity.toFixed(1)}</div>
                                                    </div>
                                                    {light.type === "HemisphereLight" ? (
                                                        <>
                                                            <div className="mb-2">
                                                                <label className="form-label">Sky Color</label>
                                                                <input
                                                                    type="color"
                                                                    className="form-control form-control-color"
                                                                    value={light.skyColor}
                                                                    onChange={(e) => handleLightChange(index, 'skyColor', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="mb-2">
                                                                <label className="form-label">Ground Color</label>
                                                                <input
                                                                    type="color"
                                                                    className="form-control form-control-color"
                                                                    value={light.groundColor}
                                                                    onChange={(e) => handleLightChange(index, 'groundColor', e.target.value)}
                                                                />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="mb-2">
                                                            <label className="form-label">Color</label>
                                                            <input
                                                                type="color"
                                                                className="form-control form-control-color"
                                                                value={light.color}
                                                                onChange={(e) => handleLightChange(index, 'color', e.target.value)}
                                                            />
                                                        </div>
                                                    )}
                                                    {light.type !== "AmbientLight" && light.position && (
                                                        <>
                                                            <div className="mb-2">
                                                                <label className="form-label">Position</label>
                                                                <div className="d-flex gap-2 align-items-center">
                                                                    <div className="flex-grow-1 d-flex gap-2">
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            placeholder="X"
                                                                            value={light.position.x}
                                                                            onChange={(e) => handlePositionChange(index, 'x', Number(e.target.value))}
                                                                        />
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            placeholder="Y"
                                                                            value={light.position.y}
                                                                            onChange={(e) => handlePositionChange(index, 'y', Number(e.target.value))}
                                                                        />
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            placeholder="Z"
                                                                            value={light.position.z}
                                                                            onChange={(e) => handlePositionChange(index, 'z', Number(e.target.value))}
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        className={`btn btn-sm ${draggingLight?.index === index && draggingLight?.type === 'position' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                        onClick={() => draggingLight?.index === index && draggingLight?.type === 'position' ? handleEndDrag() : handleStartDrag(index, 'position')}
                                                                    >
                                                                        {draggingLight?.index === index && draggingLight?.type === 'position' ? 'Stop' : 'Drag'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {(light.type === "DirectionalLight" || light.type === "SpotLight") && light.target && (
                                                                <div className="mb-2">
                                                                    <label className="form-label">Target</label>
                                                                    <div className="d-flex gap-2 align-items-center">
                                                                        <div className="flex-grow-1 d-flex gap-2">
                                                                            <input
                                                                                type="number"
                                                                                className="form-control"
                                                                                placeholder="X"
                                                                                value={light.target.x}
                                                                                onChange={(e) => handleTargetChange(index, 'x', Number(e.target.value))}
                                                                            />
                                                                            <input
                                                                                type="number"
                                                                                className="form-control"
                                                                                placeholder="Y"
                                                                                value={light.target.y}
                                                                                onChange={(e) => handleTargetChange(index, 'y', Number(e.target.value))}
                                                                            />
                                                                            <input
                                                                                type="number"
                                                                                className="form-control"
                                                                                placeholder="Z"
                                                                                value={light.target.z}
                                                                                onChange={(e) => handleTargetChange(index, 'z', Number(e.target.value))}
                                                                            />
                                                                        </div>
                                                                        <button
                                                                            className={`btn btn-sm ${draggingLight?.index === index && draggingLight?.type === 'target' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                            onClick={() => draggingLight?.index === index && draggingLight?.type === 'target' ? handleEndDrag() : handleStartDrag(index, 'target')}
                                                                        >
                                                                            {draggingLight?.index === index && draggingLight?.type === 'target' ? 'Stop' : 'Drag'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {(light.type === "PointLight" || light.type === "SpotLight" || light.type === "StrobeLight") && (
                                                        <>
                                                            <div className="mb-2">
                                                                <label className="form-label">Distance</label>
                                                                <input
                                                                    type="range"
                                                                    className="form-range"
                                                                    min="0"
                                                                    max="100"
                                                                    step="1"
                                                                    value={light.distance}
                                                                    onChange={(e) => handleLightChange(index, 'distance', Number(e.target.value))}
                                                                />
                                                                <div className="text-center">{light.distance}</div>
                                                            </div>
                                                            <div className="mb-2">
                                                                <label className="form-label">Decay</label>
                                                                <input
                                                                    type="range"
                                                                    className="form-range"
                                                                    min="0"
                                                                    max="1"
                                                                    step="0.001"
                                                                    value={light.decay}
                                                                    onChange={(e) => handleLightChange(index, 'decay', Number(e.target.value))}
                                                                />
                                                                <div className="text-center">{light.decay}</div>
                                                            </div>
                                                        </>
                                                    )}
                                                    {light.type === "StrobeLight" && (
                                                        <div className="mb-2">
                                                            <label className="form-label" htmlFor={`light-frequency-${index}`}>
                                                                Frequency
                                                            </label>
                                                            <input
                                                                id={`light-frequency-${index}`}
                                                                type="range"
                                                                className="form-range"
                                                                min={MIN_STROBE_FREQUENCY_HZ}
                                                                max={MAX_STROBE_FREQUENCY_HZ}
                                                                step="0.5"
                                                                value={light.frequencyHz ?? DEFAULT_STROBE_FREQUENCY_HZ}
                                                                onChange={(e) => handleLightChange(index, 'frequencyHz', Number(e.target.value))}
                                                            />
                                                            <div className="text-center">
                                                                {(light.frequencyHz ?? DEFAULT_STROBE_FREQUENCY_HZ).toFixed(1)} Hz
                                                            </div>
                                                        </div>
                                                    )}
                                                    {light.type === "SpotLight" && (
                                                        <>
                                                            <div className="mb-2">
                                                                <label className="form-label">Angle (degrees)</label>
                                                                <input
                                                                    type="range"
                                                                    className="form-range"
                                                                    min="0"
                                                                    max="180"
                                                                    step="0.0001"
                                                                    value={light.angle * (180 / Math.PI)}
                                                                    onChange={(e) => handleLightChange(index, 'angle', Number(e.target.value) * (Math.PI / 180))}
                                                                />
                                                                <div className="text-center">{(light.angle * (180 / Math.PI)).toFixed(4)}°</div>
                                                            </div>
                                                            <div className="mb-2">
                                                                <label className="form-label">Penumbra</label>
                                                                <input
                                                                    type="range"
                                                                    className="form-range"
                                                                    min="0"
                                                                    max="1"
                                                                    step="0.001"
                                                                    value={light.penumbra}
                                                                    onChange={(e) => handleLightChange(index, 'penumbra', Number(e.target.value))}
                                                                />
                                                                <div className="text-center">{light.penumbra.toFixed(3)}</div>
                                                            </div>
                                                        </>
                                                    )}
                                                    {(light.type === "DirectionalLight" || light.type === "SpotLight") && (
                                                        <>
                                                            <div className="mb-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={light.castShadow}
                                                                        onChange={(e) => handleLightChange(index, 'castShadow', e.target.checked)}
                                                                        id={`shadow-${index}`}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`shadow-${index}`}>
                                                                        Cast Shadows
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            {light.castShadow && (
                                                                <>
                                                                    <div className="mb-2">
                                                                        <label className="form-label">Shadow Map Size</label>
                                                                        <select
                                                                            className="form-select"
                                                                            value={light.shadows.mapSize}
                                                                            onChange={(e) => handleShadowChange(index, 'mapSize', Number(e.target.value))}
                                                                        >
                                                                            <option value="256">256</option>
                                                                            <option value="512">512</option>
                                                                            <option value="1024">1024</option>
                                                                            <option value="2048">2048</option>
                                                                            <option value="4096">4096</option>
                                                                        </select>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <label className="form-label">Near</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            value={light.shadows.near}
                                                                            onChange={(e) => handleShadowChange(index, 'near', Number(e.target.value))}
                                                                        />
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <label className="form-label">Far</label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            value={light.shadows.far}
                                                                            onChange={(e) => handleShadowChange(index, 'far', Number(e.target.value))}
                                                                        />
                                                                    </div>
                                                                    {light.type === "DirectionalLight" && (
                                                                        <div className="mb-2">
                                                                            <label className="form-label">Frustum Limit</label>
                                                                            <input
                                                                                type="number"
                                                                                className="form-control"
                                                                                value={light.shadows.frustumLimit}
                                                                                onChange={(e) => handleShadowChange(index, 'frustumLimit', Number(e.target.value))}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <div className="mb-2">
                                                                        <label className="form-label">Bias</label>
                                                                        <input
                                                                            type="number"
                                                                            step="0.00001"
                                                                            className="form-control"
                                                                            value={light.shadows.bias}
                                                                            onChange={(e) => handleShadowChange(index, 'bias', Number(e.target.value))}
                                                                        />
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <label className="form-label">Normal Bias</label>
                                                                        <input
                                                                            type="number"
                                                                            step="0.00001"
                                                                            className="form-control"
                                                                            value={light.shadows.normalBias}
                                                                            onChange={(e) => handleShadowChange(index, 'normalBias', Number(e.target.value))}
                                                                        />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sky */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('sky')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.sky ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Sky</h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.sky ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <label className="form-label">HDRI</label>
                                    <div className="d-flex gap-2 mb-2">
                                        {loadingHdri ? (
                                            <div className="d-flex align-items-center flex-grow-1">
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                <span>Loading HDRI files...</span>
                                            </div>
                                        ) : (
                                            <div>
                                                <select
                                                    className="form-select flex-grow-1 me-2"
                                                    value={settings.sky.hdriUrl}
                                                    onChange={async (e) => await handleSkyChange('hdriUrl', e.target.value)}
                                                >
                                                    <option value="">Select an HDRI...</option>
                                                    {hdriFiles.map((file, idx) => (
                                                        <option key={idx} value={file}>{file.split('/').pop()?.split('?')[0].replace('Artetra', '').replace(/%2F/g, ' ').replace(/%2/g, ' ')}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                    </div>

                                    <Button small onClick={() => hdriFileInputRef.current?.click()} disabled={uploadingHdri}>Upload HDRI</Button>
                                    <input type='file' hidden ref={hdriFileInputRef} onChange={handleUploadSkyHdriFiles} accept='.hdr' multiple />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">HDR Intensity</label>
                                    <input
                                        type="range"
                                        className="form-range"
                                        min="0"
                                        max="1"
                                        step="0.0001"
                                        value={settings.sky.hdrIntensity}
                                        onChange={handleHDRIntensityChange}
                                    />
                                    <div className="text-center">{settings.sky.hdrIntensity.toFixed(2)}</div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Sky Image</label>
                                    <div className="d-flex gap-2">
                                        {loadingSkyImages ? (
                                            <div className="d-flex align-items-center flex-grow-1">
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                <span>Loading sky images...</span>
                                            </div>
                                        ) : (
                                            <select
                                                className="form-select flex-grow-1"
                                                value={settings.sky.skyImageUrl}
                                                onChange={async (e) => await handleSkyChange('skyImageUrl', e.target.value)}
                                                disabled={uploadingSkyImage}
                                            >
                                                <option value="">Select a sky image...</option>
                                                {skyImageFiles.map((file, idx) => (
                                                    <option key={idx} value={file}>{file.split('/').pop()?.split('?')[0].replace('Artetra', '').replace(/%2F/g, ' ').replace(/%2/g, ' ')}</option>
                                                ))}
                                            </select>
                                        )}


                                    </div>
                                </div>

                                {/* <div className="mb-3">
                                    <label className="form-label">Sky Model</label>
                                    <select
                                        className="form-select"
                                        value={settings.sky.skyModelName}
                                        onChange={(e) => handleSkyChange('skyModelName', e.target.value)}
                                    >
                                        <option value="">Select a mesh...</option>
                                        {getMeshNames(appData).map((name, idx) => (
                                            <option key={idx} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div> */}

                                {/*<div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={settings.sky.hdriVisible}
                                            onChange={(e) => handleSkyChange('hdriVisible', e.target.checked)}
                                            id="hdriVisible"
                                        />
                                        <label className="form-check-label" htmlFor="hdriVisible">
                                            Show HDRI
                                        </label>
                                    </div>
                                </div>*/}

                                <Button small onClick={() => skyImageFileInputRef.current?.click()} disabled={uploadingSkyImage}>Upload Sky Image</Button>
                                <input type='file' hidden ref={skyImageFileInputRef} onChange={handleUploadSkyImageFiles} accept='.jpg,.jpeg,.png' multiple />
                            </div>
                        </div>
                    </div>

                    {/* Light Map */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('lightMap')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.lightMap ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Light Map <span style={{ fontSize: '12px', color: 'white', fontWeight: 'lighter' }}>*use only in extreme cases!</span></h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.lightMap ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <label className="form-label">Selected Meshes</label>
                                    <div className="dropdown" id="mesh-dropdown">
                                        <button
                                            className="btn btn-secondary dropdown-toggle w-100"
                                            type="button"
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        >
                                            {settings.lightMap.selectedMeshes.length > 0
                                                ? `${settings.lightMap.selectedMeshes.length} meshes selected`
                                                : 'Select meshes...'}
                                        </button>
                                        <ul className={`dropdown-menu w-100 ${isDropdownOpen ? 'show' : ''}`} style={{
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            display: isDropdownOpen ? 'block' : 'none'
                                        }}>
                                            {getMeshNames(appData).map((name, idx) => (
                                                <li key={idx}>
                                                    <div className="dropdown-item">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                checked={settings.lightMap.selectedMeshes.includes(name)}
                                                                onChange={(e) => {
                                                                    const newSelectedMeshes = e.target.checked
                                                                        ? [...settings.lightMap.selectedMeshes, name]
                                                                        : settings.lightMap.selectedMeshes.filter(mesh => mesh !== name);
                                                                    updateSettings(prev => ({
                                                                        ...prev,
                                                                        lightMap: {
                                                                            ...prev.lightMap,
                                                                            selectedMeshes: newSelectedMeshes
                                                                        }
                                                                    }));
                                                                }}
                                                                id={`mesh-${idx}`}
                                                            />
                                                            <label className="form-check-label" htmlFor={`mesh-${idx}`}>
                                                                {name}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Light Map URL</label>
                                    <div className="d-flex gap-2 mb-2">
                                        {loadingLightMaps ? (
                                            <div className="d-flex align-items-center flex-grow-1">
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                <span>Loading light maps...</span>
                                            </div>
                                        ) : (
                                            <select
                                                className="form-select flex-grow-1"
                                                value={settings.lightMap.lightMapUrl}
                                                onChange={(e) => updateSettings(prev => ({
                                                    ...prev,
                                                    lightMap: {
                                                        ...prev.lightMap,
                                                        lightMapUrl: e.target.value
                                                    }
                                                }))}
                                            >
                                                <option value="">Select a light map...</option>
                                                {lightMapFiles.map((file, idx) => (
                                                    <option key={idx} value={file}>{file.split('/').pop()?.split('?')[0].replace('Artetra', '').replace(/%2F/g, ' ').replace(/%2/g, ' ')}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <Button small onClick={() => lightMapFileInputRef.current?.click()} disabled={uploadingLightMap}>Upload Light Map</Button>
                                    <input type='file' hidden ref={lightMapFileInputRef} onChange={handleUploadLightMapFiles} accept='.png' multiple />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Material Overrides */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('materialOverrides')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.materialOverrides ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Material Overrides</h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.materialOverrides ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <div className="d-flex flex-wrap gap-2">
                                        {["map", "roughnessMap", "metalnessMap", "normalMap"].map((type) => (
                                            <button
                                                key={type}
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleAddMaterialOverride(type as MaterialOverrideType)}
                                            >
                                                Add {type}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="border rounded p-2">
                                    {settings.materialOverrides.map((override, index) => (
                                        <div key={index} className="mb-3 p-2 bg-dark rounded">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="m-0">
                                                    {override.channel === 'map' ? 'Diffuse/Albedo Map' :
                                                        override.channel === 'roughnessMap' ? 'Roughness Map' :
                                                            override.channel === 'metalnessMap' ? 'Metalness Map' :
                                                                'Normal Map'}
                                                </h6>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleRemoveMaterialOverride(index)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                            <div className="mt-2">
                                                <div className="mb-2">
                                                    <label className="form-label">Mesh Name</label>
                                                    <select
                                                        className="form-select"
                                                        value={override.meshName}
                                                        onChange={(e) => handleMaterialOverrideChange(index, 'meshName', e.target.value)}
                                                    >
                                                        <option value="">Select a mesh...</option>
                                                        {getMeshNames(appData).map((name, idx) => (
                                                            <option key={idx} value={name}>{name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="mb-2">
                                                    <label className="form-label">Texture URL</label>
                                                    <div className="d-flex gap-2 mb-2">
                                                        {loadingTextures ? (
                                                            <div className="d-flex align-items-center flex-grow-1">
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                <span>Loading textures...</span>
                                                            </div>
                                                        ) : (
                                                            <select
                                                                className="form-select flex-grow-1"
                                                                value={override.textureUrl}
                                                                onChange={(e) => handleMaterialOverrideChange(index, 'textureUrl', e.target.value)}
                                                            >
                                                                <option value="">Select a texture...</option>
                                                                {textureFiles.map((file, idx) => (
                                                                    <option key={idx} value={file}>{file.split('/').pop()?.split('?')[0].replace('Artetra', '').replace(/%2F/g, ' ').replace(/%2/g, ' ')}</option>
                                                                ))}
                                                            </select>
                                                        )}


                                                    </div>
                                                    <Button small onClick={() => textureFileInputRef.current?.click()} disabled={uploadingTexture}>Upload Texture</Button>
                                                    <input type='file' hidden ref={textureFileInputRef} onChange={handleUploadTextureFiles} accept='.png' multiple />
                                                </div>
                                                <div className="mb-2">
                                                    <label className="form-label">Repeat</label>
                                                    <input
                                                        type="range"
                                                        className="form-range"
                                                        min="0.1"
                                                        max="50"
                                                        step="0.1"
                                                        value={override.repeat}
                                                        onChange={(e) => handleMaterialOverrideChange(index, 'repeat', Number(e.target.value))}
                                                    />
                                                    <div className="text-center">{override.repeat.toFixed(1)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Post-Processing */}
                    <div className="mb-4">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => toggleSection('postProcessing')}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className={`bi bi-chevron-${collapsedSections.postProcessing ? 'right' : 'down'} me-2`}></i>
                                <h5 className="m-0">▼ Post-Processing</h5>
                            </div>
                        </div>
                        <div className={`collapse ${!collapsedSections.postProcessing ? 'show' : ''}`}>
                            <div className="ps-3 pt-3">
                                <div className="mb-3">
                                    <div className="d-flex flex-wrap gap-2">
                                        {["render", "fxaa", "bloom", "lut", "dotScreen", "noise", "glitch"]
                                            .filter(passType => !settings.renderSettings.postProcessing.some(pass => pass.type === passType))
                                            .map((pass) => (
                                                <button
                                                    key={pass}
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleAddPass(pass as any)}
                                                >
                                                    Add {pass === "dotScreen" ? "dot-screen" : pass}
                                                </button>
                                            ))}
                                    </div>
                                </div>
                                <div
                                    className="border rounded p-2"
                                    onDragOver={handleDragOver}
                                >
                                    {/* Drop zone at the beginning */}
                                    <div
                                        className="dropzone py-1 text-center"
                                        onDragOver={handleDragOver}
                                        onDragEnter={() => handleDragEnterDropZone(0)}
                                        onDragLeave={handleDragLeaveDropZone}
                                        onDrop={(e) => handleDrop(e, 0)}
                                        style={{
                                            height: '10px',
                                            backgroundColor: draggedItem !== null && dragOverIndex === 0
                                                ? 'rgba(75, 192, 192, 0.6)'
                                                : draggedItem !== null
                                                    ? 'rgba(75, 192, 192, 0.2)'
                                                    : 'transparent',
                                            transition: 'background-color 0.2s ease',
                                            borderRadius: '4px'
                                        }}
                                    />

                                    {visiblePostProcessingPasses.map(({ pass, sourceIndex }, index) => (
                                        <React.Fragment key={`${pass.type}-${sourceIndex}`}>
                                            <div
                                                draggable
                                                onDragStart={() => handleDragStart(sourceIndex)}
                                                className={`d-flex justify-content-between align-items-center p-2 bg-dark rounded ${draggedItem === sourceIndex ? 'opacity-50' : ''}`}
                                                style={{
                                                    cursor: 'move',
                                                    borderLeft: '4px solid rgba(75, 192, 192, 0.7)'
                                                }}
                                            >
                                                <span>{pass.type}</span>
                                                <div>
                                                    {pass.type === 'bloom' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-2"
                                                            onClick={() => setEditingBloom(editingBloom === sourceIndex ? null : sourceIndex)}
                                                        >
                                                            {editingBloom === sourceIndex ? 'Done' : 'Edit'}
                                                        </button>
                                                    )}
                                                    {pass.type === 'lut' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-2"
                                                            onClick={() => setEditingLUT(editingLUT === sourceIndex ? null : sourceIndex)}
                                                        >
                                                            {editingLUT === sourceIndex ? 'Done' : 'Edit'}
                                                        </button>
                                                    )}
                                                    {pass.type === 'dotScreen' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-2"
                                                            onClick={() => setEditingDotScreen(editingDotScreen === sourceIndex ? null : sourceIndex)}
                                                        >
                                                            {editingDotScreen === sourceIndex ? 'Done' : 'Edit'}
                                                        </button>
                                                    )}
                                                    {pass.type === 'noise' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-2"
                                                            onClick={() => setEditingNoise(editingNoise === sourceIndex ? null : sourceIndex)}
                                                        >
                                                            {editingNoise === sourceIndex ? 'Done' : 'Edit'}
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleRemovePass(sourceIndex)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Drop zone after each pass */}
                                            <div
                                                className="dropzone py-1 mt-1 mb-1 text-center"
                                                onDragOver={handleDragOver}
                                                onDragEnter={() => handleDragEnterDropZone(index + 1)}
                                                onDragLeave={handleDragLeaveDropZone}
                                                onDrop={(e) => handleDrop(e, index + 1)}
                                                style={{
                                                    height: '10px',
                                                    backgroundColor: draggedItem !== null && dragOverIndex === index + 1
                                                        ? 'rgba(75, 192, 192, 0.6)'
                                                        : draggedItem !== null
                                                            ? 'rgba(75, 192, 192, 0.2)'
                                                            : 'transparent',
                                                    transition: 'background-color 0.2s ease',
                                                    borderRadius: '4px'
                                                }}
                                            />
                                        </React.Fragment>
                                    ))}
                                </div>
                                {editingBloom !== null && (
                                    <div className="mt-3 p-3 bg-dark rounded">
                                        <h6 className="mb-3">Bloom Settings</h6>
                                        <div className="mb-3">
                                            <label className="form-label">Bloom Threshold</label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0.001"
                                                max="1.0"
                                                step="0.001"
                                                value={(settings.renderSettings.postProcessing[editingBloom] as BloomPass).threshold}
                                                onChange={handleBloomThresholdChange}
                                            />
                                            <div className="text-center">{((settings.renderSettings.postProcessing[editingBloom] as BloomPass).threshold).toFixed(3)}</div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Bloom Strength</label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0.0001"
                                                max="5.0"
                                                step="0.0001"
                                                value={(settings.renderSettings.postProcessing[editingBloom] as BloomPass).strength}
                                                onChange={handleBloomStrengthChange}
                                            />
                                            <div className="text-center">{((settings.renderSettings.postProcessing[editingBloom] as BloomPass).strength).toFixed(4)}</div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Bloom Radius</label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0.001"
                                                max="1.0"
                                                step="0.001"
                                                value={(settings.renderSettings.postProcessing[editingBloom] as BloomPass).radius}
                                                onChange={handleBloomRadiusChange}
                                            />
                                            <div className="text-center">{((settings.renderSettings.postProcessing[editingBloom] as BloomPass).radius).toFixed(3)}</div>
                                        </div>
                                    </div>
                                )}
                                {editingLUT !== null && (
                                    <div className="mt-3 p-3 bg-dark rounded">
                                        <h6 className="mb-3">LUT Settings</h6>
                                        <div className="mb-3">
                                            <label className="form-label">LUT File</label>
                                            <div className="d-flex gap-2">
                                                {loadingLut ? (
                                                    <div className="d-flex align-items-center flex-grow-1">
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        <span>Loading LUT files...</span>
                                                    </div>
                                                ) : (
                                                    <select
                                                        className="form-select flex-grow-1"
                                                        value={(settings.renderSettings.postProcessing[editingLUT] as LUTPass).cubeMapUrl}
                                                        onChange={handleLUTFileSelect}
                                                    >
                                                        <option value="">Select a LUT file...</option>
                                                        {lutFiles.map((file, idx) => (
                                                            <option key={idx} value={file}>{file.split('/').pop()?.split('?')[0].replace('Artetra', '').replace(/%2F/g, ' ').replace(/%2/g, ' ')}</option>
                                                        ))}
                                                    </select>
                                                )}


                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Intensity</label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0"
                                                max="2"
                                                step="0.01"
                                                value={(settings.renderSettings.postProcessing[editingLUT] as LUTPass).intensity}
                                                onChange={handleLUTIntensityChange}
                                            />
                                            <div className="text-center">{editingLUT !== null && settings.renderSettings.postProcessing[editingLUT] && (settings.renderSettings.postProcessing[editingLUT] as LUTPass)?.intensity !== undefined ? ((settings.renderSettings.postProcessing[editingLUT] as LUTPass).intensity).toFixed(2) : '0.00'}</div>
                                        </div>
                                    </div>
                                )}
                                {editingDotScreen !== null && (
                                    <div className="mt-3 p-3 bg-dark rounded">
                                        <h6 className="mb-3">Dot Screen Settings</h6>
                                        <div className="mb-3">
                                            <label className="form-label">Scale</label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0.001"
                                                max="1.0"
                                                step="0.001"
                                                value={(settings.renderSettings.postProcessing[editingDotScreen] as DotScreenPass).scale}
                                                onChange={handleDotScreenScaleChange}
                                            />
                                            <div className="text-center">{((settings.renderSettings.postProcessing[editingDotScreen] as DotScreenPass).scale).toFixed(1)}</div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Angle</label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0"
                                                max="6.28"
                                                step="0.01"
                                                value={(settings.renderSettings.postProcessing[editingDotScreen] as DotScreenPass).angle}
                                                onChange={handleDotScreenAngleChange}
                                            />
                                            <div className="text-center">{((settings.renderSettings.postProcessing[editingDotScreen] as DotScreenPass).angle).toFixed(2)} rad</div>
                                        </div>
                                    </div>
                                )}
                                {editingNoise !== null && (
                                    <div className="mt-3 p-3 bg-dark rounded">
                                        <h6 className="mb-3">Noise Settings</h6>
                                        <div className="mb-3">
                                            <label className="form-label">Amount</label>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min="0"
                                                max="5"
                                                step="0.01"
                                                value={(settings.renderSettings.postProcessing[editingNoise] as NoisePass).amount}
                                                onChange={handleNoiseAmountChange}
                                            />
                                            <div className="text-center">{((settings.renderSettings.postProcessing[editingNoise] as NoisePass).amount).toFixed(2)}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-3">
                                    <label className="form-label">Artwork Map Intensity</label>
                                    <input
                                        type="range"
                                        className="form-range"
                                        min="0"
                                        max="2"
                                        step="0.01"
                                        value={settings.renderSettings.artworkMapIntensity ?? 1.0}
                                        onChange={handleArtworkMapIntensityChange}
                                    />
                                    <div className="text-center">{(settings.renderSettings.artworkMapIntensity ?? 1.0).toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Apply */}
                <div className="d-flex justify-content-end">
                    <Button onClick={undoSettings} className='me-3' disabled={renderSettingsQueue.isEmpty()}>Undo</Button>
                    {/* Legacy functionality kept as requested:
                    <Button onClick={onPublishMood} className='me-3'>
                        {availableMoods.find(mood => mood.id === selectedMood)?.mood.published ? 'Unpublish' : 'Publish'}
                    </Button>
                    */}
                    <Button onClick={handleApply}>Save Settings</Button>
                </div>
            </div>
        </div>
    );
}; 
